import { useState } from "react";
import { isEmailAddress, isZenkakuKana, isURL } from "~/helpers/validate";
import firebase from "~/plugins/firebase";
import { specialistsCollection } from "~/const/firestore";
import { Specialist, SpecialistAuth } from "~/types/specialist";
import { useHistory } from "react-router-dom";
import { ROUTES } from "~/const/route";
import { useDispatch } from "react-redux";
import { login } from "~/redux/modules/specialist/auth";
import { Event } from "~/types/component";

type SpecialistForm = Specialist & SpecialistAuth;
// eslint-disable-next-line no-unused-vars
type SpecialistErrorMessage = { [P in keyof SpecialistForm]?: string };

export const useModule = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  // states
  const [specialist, setSpecialist] = useState<SpecialistForm>({
    isPublic: false,
    uid: "",
    email: "",
  });
  const [errorAlertMessage, setErrorAlertMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<SpecialistErrorMessage>({});
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // setters
  const changeValue = (e: Event, key: keyof SpecialistForm) => {
    setErrorMessage({ ...errorMessage, [key]: undefined });
    setSpecialist({ ...specialist, [key]: e.target.value });
  };

  // actions
  const openModalWithValidate = ({ auth }: { auth: boolean }) => {
    // エラーメッセージをリセット
    setErrorAlertMessage("");

    let required: (keyof SpecialistForm)[] = [
      "firstName",
      "lastName",
      "firstNameKana",
      "lastNameKana",
      "sex",
      "business",
      "contactMethod1",
      "contact1",
    ];
    let _errorMessage: SpecialistErrorMessage = {};
    let _noError: boolean = true;

    // 名前
    if (specialist.firstNameKana && !isZenkakuKana(specialist.firstNameKana)) {
      _errorMessage = {
        ..._errorMessage,
        firstNameKana: "全角カタカナで入力してください",
      };
      _noError = false;
    }
    if (specialist.lastNameKana && !isZenkakuKana(specialist.lastNameKana)) {
      _errorMessage = {
        ..._errorMessage,
        lastNameKana: "全角カタカナで入力してください",
      };
      _noError = false;
    }

    if (auth === true) {
      // 必須項目の追加
      required = required.concat(["email", "password", "confirmPassword"]);

      // email
      if (specialist.email && !isEmailAddress(specialist.email)) {
        _errorMessage = {
          ..._errorMessage,
          email: "正しいメールアドレスを記入してください",
        };
        _noError = false;
      }

      // パスワード
      if (specialist.password && specialist.password.length < 6) {
        _errorMessage = {
          ..._errorMessage,
          password: "6文字以上で入力してください",
        };
        _noError = false;
      }
      if (specialist.password !== specialist.confirmPassword) {
        _errorMessage = {
          ..._errorMessage,
          password: "パスワードが一致しません",
          confirmPassword: "パスワードが一致しません",
        };
        _noError = false;
      }
    }

    // ホームページ
    if (specialist.hp && !isURL(specialist.hp)) {
      _errorMessage = {
        ..._errorMessage,
        hp: "正しいURLを記入してください",
      };
      _noError = false;
    }

    // 必須項目
    for (let key of required) {
      if (specialist[key] === undefined || specialist[key] === "") {
        _errorMessage = { ..._errorMessage, [key]: "この項目は必須項目です。" };
        _noError = false;
      }
    }

    // エラーがなければ、確認モーダルを開く
    if (_noError) {
      setOpenModal(true);
    } else {
      setTimeout(() => {
        setErrorAlertMessage("入力内容に問題があります");
        setErrorMessage(_errorMessage);
      }, 100);
    }
  };
  // 確認モーダルを閉じる
  const closeModal = () => setOpenModal(false);
  // 専門家情報をfirestoreに保存
  const saveFirestore = async (uid: string, data: SpecialistForm) => {
    // エラーメッセージをリセット
    setErrorAlertMessage("");

    let success = false; // 処理が成功しているかどうか
    const _cpData = data;
    delete _cpData.password;
    delete _cpData.confirmPassword;
    _cpData.uid = uid; // ユーザーID
    _cpData.isPublic = false; // 非公開
    _cpData.created = new Date(); // 登録日
    const _data: Specialist = _cpData;
    try {
      await specialistsCollection.doc(uid).set(_data);
      success = true;
    } catch (err) {
      setErrorAlertMessage("エラーが発生しました");
    }
    return success;
  };
  // 専門家情報の登録
  const registorSpecialist = async () => {
    setLoading(true);
    setOpenModal(false);
    if (specialist.email && specialist.password) {
      try {
        const { user } = await firebase
          .auth()
          .createUserWithEmailAndPassword(
            specialist.email,
            specialist.password
          );
        if (user) {
          const uid = user.uid;
          const success = await saveFirestore(uid, specialist);
          if (success) {
            dispatch(login(uid));
            setTimeout(() => history.push(ROUTES.SPECIALIST.FINISH), 100);
          }
        }
      } catch (err) {
        let _errorMessage: SpecialistErrorMessage = {};
        let _errorAlertMessage: string = "入力内容に問題があります";
        switch (err.code) {
          case "auth/email-already-in-use":
            _errorMessage = {
              ..._errorMessage,
              email: "このメールアドレスはすでに使用されています。",
            };
            break;
          case "auth/invalid-email":
            _errorMessage = {
              ..._errorMessage,
              email: "このメールアドレスは使用できません。",
            };
            break;
          case "auth/weak-password":
            _errorMessage = {
              ..._errorMessage,
              password: "6文字以上で入力してください。",
            };
            break;
          default:
            _errorAlertMessage = "エラーが発生しました";
        }
        setErrorMessage(_errorMessage);
        setErrorAlertMessage(_errorAlertMessage);
      }
    } else {
      setErrorMessage({
        email: "この項目は必須項目です。",
        password: "この項目は必須項目です。",
      });
      setErrorAlertMessage("入力内容に問題があります");
    }
    setLoading(false);
  };
  // 専門家情報の更新
  const updateSpecialist = async (uid: string | null) => {
    setLoading(true);
    setOpenModal(false);
    if (uid) {
      // 更新時は非公開設定
      const _success = await saveFirestore(uid, {
        ...specialist,
        isPublic: false,
      });
      if (_success) {
        history.push(ROUTES.SPECIALIST.FINISH);
      }
    }
    setLoading(false);
  };

  return {
    states: { specialist, errorAlertMessage, errorMessage, openModal, loading },
    setters: { changeValue, setSpecialist },
    actions: {
      openModalWithValidate,
      closeModal,
      registorSpecialist,
      updateSpecialist,
    },
  };
};
